import DoneAllIcon from "@mui/icons-material/DoneAll";
import {
  AvatarGroup,
  Box,
  BoxProps,
  CircularProgress,
  FormHelperText,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { isEmpty } from "lodash-es";
import { useCallback, useMemo, useState } from "react";

import AppIcon from "@/common/components/Icons/AppIcon";
import { FeatureName } from "@/common/constants/featureName";
import { useChatContext } from "@/common/contexts/chat";
import { DatetimeHelper } from "@/common/helpers/datetime";
import { useChatParticipants } from "@/common/hooks/communication/useChatParticipants";
import { useAppThunkDispatch } from "@/common/hooks/redux";
import useAppSnackbar from "@/common/hooks/useAppSnackbar";
import { chatMessageService } from "@/common/services/chatMessage";
import { ValidationHelper, ValidationInfo } from "@/common/validation";
import { featureManager } from "@/config/features";
import {
  AppPermission,
  ChatDto,
  ChatHistoryItemDto,
  ChatMessageRootNodeDto,
  ChatParticipantDto,
  ChatSettingsDto,
  TagEntityType,
} from "@/core/api/generated/v1";
import * as chatHistorySlice from "@/store/communication/chatHistorySlice";
import * as chatMessagesSlice from "@/store/communication/chatMessagesSlice";

import AppVisibilitySensor from "../../AppVisibilitySensor";
import AuthorizedElement from "../../Auth/AuthorizedElement";
import AuthorizedMenuItem from "../../Auth/AuthorizedMenuItem";
import AppIconButton from "../../Button/AppIconButton";
import ChatParticipantAvatar from "../ChatParticipant/ChatParticipantAvatar";
import ChatParticipantListPopover from "../ChatParticipant/ChatParticipantListPopover";
import ChatParticipantsModal from "../ChatParticipant/ChatParticipantsModal";
import GeneralAttachedTagsDisplay from "../General/GeneralTag/GeneralAttachedTagsDisplay";
import GeneralAttachedTagsOfEntityEditModal from "../General/GeneralTag/GeneralAttachedTagsOfEntityEditModal";
import ChatMessageAttachments from "./Attachments/ChatMessageAttachments";
import ChatMessageBodyView from "./ChatMessageBodyView";
import ChatMessageReadByInfoModal from "./ChatMessageReadByInfoModal";
import ChatMessageInput from "./Input/ChatMessageInput";

const defaultDisplayProps = {
  actions: true,
};

interface OwnProps {
  chat: ChatDto;
  item: ChatHistoryItemDto;
  showMessageActions?: boolean;
  chatSettings?: ChatSettingsDto;
  currentParticipant?: ChatParticipantDto;
  participant?: ChatParticipantDto | null;
  participants?: ChatParticipantDto[];
  displayProps?: Partial<typeof defaultDisplayProps>;
  sx?: BoxProps["sx"];
}

interface Props extends OwnProps {}

export default function ChatMessage({
  chat,
  item,
  chatSettings,
  currentParticipant,
  participant,
  participants,
  displayProps,
  sx,
}: Props) {
  displayProps = {
    ...defaultDisplayProps,
    ...displayProps,
  };

  const message = item.message!;

  const thunkDispatch = useAppThunkDispatch();
  const { enqueueSnackbar } = useAppSnackbar();

  const chatContext = useChatContext();
  const { participantsMap } = useChatParticipants(item.chatId!);

  const [validation, setValidation] = useState<ValidationInfo>(new ValidationInfo());
  const [editValidation, setEditValidation] = useState<ValidationInfo>(new ValidationInfo());
  const [isEditing, setIsEditing] = useState(false);
  const [isPinLoading] = useState(false);

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [acknowledgePoperOpenAnchorEl, setAcknowledgePoperOpenAnchorEl] =
    useState<HTMLElement | null>(null);
  const isMenuOpen = Boolean(menuAnchorEl);
  const isAcknowledgePoperOpen = Boolean(acknowledgePoperOpenAnchorEl);
  const [isEditTagsModalOpen, setIsEditTagsModalOpen] = useState(false);
  const [isReadByModalOpen, setIsReadByModalOpen] = useState(false);
  const [isViewParticipantsModalOpen, setIsViewParticipantsModalOpen] = useState(false);

  const isReadStatusEnabled =
    featureManager.isEnabled(FeatureName.ChatMessageReadStatus) && !!currentParticipant;

  const acknowledgements = useMemo(
    () =>
      message.acknowledgements?.map((x) => ({
        ...x,
        participant: participants?.find((p) => p.id === x.participantId),
      })),
    [message, participants],
  );
  const isAcknowledged = useMemo(
    () =>
      message.acknowledgements!.some(
        (x) => x.participantId === currentParticipant?.id && x.isAcknowledged,
      ),
    [message, currentParticipant],
  );
  const isShowReadBy = useMemo(
    () =>
      isReadStatusEnabled &&
      !!currentParticipant &&
      message.readByInfo!.some((x) => x.participantId !== currentParticipant?.id),
    [isReadStatusEnabled, message, currentParticipant],
  );
  const isReadByMe = useMemo(
    () =>
      currentParticipant &&
      message.readByInfo!.some((x) => x.participantId === currentParticipant?.id),
    [message, currentParticipant],
  );

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleEdit = () => {
    setIsEditing(true);
    handleMenuClose();
  };

  const handleEdited = async (data: { body: string; rootNode: ChatMessageRootNodeDto }) => {
    try {
      await thunkDispatch(
        chatMessagesSlice.updateChatMessage({
          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
          chatId: message.chatId!,
          messageId: message.id!,
          updateChatMessageDto: {
            body: data.body,
            rootNode: data.rootNode,
            attachments: message.attachments,
          },
        }),
      );
      setEditValidation(new ValidationInfo());
      setIsEditing(false);
    } catch (err) {
      setEditValidation(ValidationHelper.handleApiErrorResponse(err));
      throw err;
    }
  };

  const handleDelete = useCallback(async () => {
    try {
      await thunkDispatch(
        chatMessagesSlice.deleteChatMessage({
          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
          chatId: message.chatId!,
          messageId: message.id!,
        }),
      );
      setValidation(new ValidationInfo());
    } catch (err) {
      setValidation(ValidationHelper.handleApiErrorResponse(err));
    } finally {
      handleMenuClose();
    }
  }, [message]);

  const handleAcknowledge = useCallback(async () => {
    try {
      await thunkDispatch(
        chatMessagesSlice.acknowledgeChatMessage({
          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
          chatId: message.chatId!,
          messageId: message.id!,
        }),
      );
      setValidation(new ValidationInfo());
    } catch (err) {
      setValidation(ValidationHelper.handleApiErrorResponse(err));
    } finally {
      handleMenuClose();
    }
  }, [message]);

  const handlePin = useCallback(async () => {
    try {
      await thunkDispatch(
        chatHistorySlice.pinChatHistoryItem({
          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
          chatId: message.chatId!,
          itemId: item.id!,
        }),
      );
      enqueueSnackbar("Message pinned.", { variant: "success" });
    } catch (err) {
      const validation2 = ValidationHelper.handleApiErrorResponse(err);
      if (validation2.hasErrors) {
        enqueueSnackbar(`Error: ${validation2.getErrorsAsString()}`, { variant: "error" });
      }
    } finally {
      handleMenuClose();
    }
  }, [item]);

  const handleUnpin = useCallback(async () => {
    try {
      await thunkDispatch(
        chatHistorySlice.unpinChatHistoryItem({
          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
          chatId: message.chatId!,
          itemId: item.id!,
        }),
      );
      enqueueSnackbar("Message unpinned.", { variant: "success" });
    } catch (err) {
      const validation2 = ValidationHelper.handleApiErrorResponse(err);
      if (validation2.hasErrors) {
        enqueueSnackbar(`Error: ${validation2.getErrorsAsString()}`, { variant: "error" });
      }
    } finally {
      handleMenuClose();
    }
  }, []);

  const participantName = participant?.personName?.name || participant?.email || "Unknown";
  const isMyMessage = message.participantId === currentParticipant?.id;

  return (
    <Box
      sx={{
        backgroundColor: (th) =>
          !isReadStatusEnabled || isReadByMe
            ? undefined
            : th.palette.background.activeItemBackground,
        transition: "background-color 0.2s ease-in-out",
        // borderRadius: (th) => th.shapeCustom.borderRadiusCard,
        borderRadius: 0,
        ...sx,
      }}
    >
      {/* Mark message as read if it was at viewport for N ms */}
      <AppVisibilitySensor
        debounceParams={{
          waitMs: 100, // use small value to ensure that message is marked as read as soon as possible, especially when user scrolls fast
          options: {
            leading: false,
            trailing: true,
          },
          isCancelWhenInvisible: true,
        }}
        onChange={(isVisible) => {
          if (chatContext.chatPerms?.shouldMarkMessagesAsRead && isVisible && !isReadByMe) {
            chatMessageService.queueMarkChatMessageAsRead({
              chatId: message.chatId!,
              messageId: message.id!,
            });
          }
        }}
      >
        <Stack
          direction='row'
          gap={{ xxs: 1, md: 2 }}
          sx={{
            alignItems: "flex-start",
            pr: 1,
          }}
        >
          <ChatParticipantAvatar
            chatId={message.chatId}
            participant={participant}
            size={{ xxs: 32, md: 48 }}
            sx={{ fontSize: { xxs: "0.9rem", md: "1.25rem" } }}
          />

          <Stack direction='column' sx={{ flex: 1, overflow: "hidden", gap: { xxs: 1, md: 0 } }}>
            {/* Message header */}
            {/* <Stack direction='row' gap={2} sx={{ alignItems: "center" }}> */}
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 2 }}>
              {/* Title */}
              <Stack
                sx={{
                  flexDirection: { xxs: "column", md: "row" },
                  flexWrap: "wrap",
                  alignItems: { xxs: "flex-start", md: "flex-start" },
                  gap: 0,
                }}
              >
                <Stack sx={{ mr: 1 }}>
                  <Typography component='div' variant='body1' sx={{ fontWeight: "bold" }}>
                    <span>{participantName}</span>

                    {/* Participant parties */}
                    {!isEmpty(participant?.parties) && (
                      <Tooltip title='Participant party'>
                        <Typography
                          sx={{ ml: 0.5, cursor: "pointer" }}
                          component='span'
                          variant='caption'
                          onClick={() => setIsViewParticipantsModalOpen(true)}
                        >
                          ({participant?.parties?.map((x) => x.party?.name).join(", ")})
                        </Typography>
                      </Tooltip>
                    )}
                  </Typography>
                </Stack>

                <Stack gap={0.5} sx={{ order: { xxs: -1, md: 0 }, flexDirection: "row" }}>
                  <Typography component='span' variant='caption' color='text.secondary'>
                    {DatetimeHelper.humanizeDateRangeDurationFromNow(message.createdAt, {
                      isSuffix: true,
                    })}
                  </Typography>

                  {message.isEdited && (
                    <Typography component='span' variant='caption' color='text.secondary'>
                      (edited)
                    </Typography>
                  )}
                </Stack>
              </Stack>

              {/* Actions */}
              {displayProps.actions && chatContext.chatPerms?.canViewMessageActions && (
                <Stack direction='row' sx={{ flex: 1, justifyContent: "flex-end" }}>
                  <AppIconButton
                    sx={{ p: 0 }}
                    edge={false}
                    shape='round'
                    onClick={(e) => setMenuAnchorEl(e.currentTarget)}
                  >
                    <AppIcon of='moreVert' />
                  </AppIconButton>

                  <Menu anchorEl={menuAnchorEl} open={isMenuOpen} onClose={handleMenuClose}>
                    {chatSettings?.allowAcknowledgement &&
                      chatContext.chatPerms?.canAcknowledgeMessage &&
                      !isAcknowledged && (
                        <MenuItem dense onClick={handleAcknowledge}>
                          <ListItemIcon>
                            <AppIcon of='acknowledge' fontSize='small' />
                          </ListItemIcon>
                          <ListItemText>Acknowledge</ListItemText>
                        </MenuItem>
                      )}
                    {chatSettings?.allowPinnedItems && chatContext.chatPerms?.canPinItems && (
                      <AuthorizedMenuItem
                        permissions={[AppPermission.FleetAppAccess]}
                        dense
                        onClick={() => (item.isPinned ? handleUnpin() : handlePin())}
                      >
                        <ListItemIcon>
                          <AppIcon of='pin' fontSize='small' />
                        </ListItemIcon>
                        <ListItemText>{item.isPinned ? "Unpin" : "Pin"}</ListItemText>
                        {isPinLoading && <CircularProgress color='inherit' size={20} />}
                      </AuthorizedMenuItem>
                    )}
                    {isMyMessage &&
                      chatSettings?.allowEditMessages &&
                      chatContext.chatPerms?.canUpdateMessage && (
                        <MenuItem dense onClick={handleEdit}>
                          <ListItemIcon>
                            <AppIcon of='edit' fontSize='small' />
                          </ListItemIcon>
                          <ListItemText>Edit</ListItemText>
                        </MenuItem>
                      )}
                    {chatContext.chatPerms?.canUpdateMessageTags && (
                      <AuthorizedMenuItem
                        permissions={[AppPermission.FleetAppAccess]}
                        dense
                        onClick={() => {
                          setIsEditTagsModalOpen(true);
                          handleMenuClose();
                        }}
                      >
                        <ListItemIcon>
                          <AppIcon of='tag' fontSize='small' />
                        </ListItemIcon>
                        <ListItemText>Edit tags</ListItemText>
                      </AuthorizedMenuItem>
                    )}
                    {isMyMessage &&
                      chatSettings?.allowDeleteMessages &&
                      chatContext.chatPerms?.canDeleteMessage && (
                        <AuthorizedMenuItem
                          permissions={[AppPermission.FleetAppAccess]}
                          dense
                          onClick={handleDelete}
                        >
                          <ListItemIcon>
                            <AppIcon of='delete' fontSize='small' />
                          </ListItemIcon>
                          <ListItemText>Delete</ListItemText>
                        </AuthorizedMenuItem>
                      )}
                  </Menu>
                </Stack>
              )}
            </Box>

            {/* Message content */}
            {!isEditing && (
              <Box>
                {/* Main content */}
                <>
                  <ChatMessageBodyView message={message} />
                  <ChatMessageAttachments message={message} />
                </>

                {/* Secondary content */}
                <Stack direction='row' sx={{ justifyContent: "space-between" }}>
                  <Stack direction='column'>
                    {/* Acknowledgements */}
                    {acknowledgements && acknowledgements.length !== 0 && (
                      <>
                        <Stack
                          direction='row'
                          spacing={1}
                          sx={{ alignItems: "center" }}
                          onMouseEnter={(e) => setAcknowledgePoperOpenAnchorEl(e.currentTarget)}
                          onMouseLeave={() => setAcknowledgePoperOpenAnchorEl(null)}
                        >
                          <DoneAllIcon color='success' fontSize='small' />
                          <Typography component='span' variant='caption' color='text.secondary'>
                            {acknowledgements.length < 10 ? acknowledgements.length : "10+"}{" "}
                            acknowledged
                          </Typography>
                        </Stack>

                        <ChatParticipantListPopover
                          chatId={message?.chatId}
                          participantIds={acknowledgements.map((x) => x.participantId!)}
                          getParticipantData={(participantId) => ({
                            datetime: acknowledgements?.find(
                              (x) => x.participantId === participantId,
                            )?.acknowledgedAt,
                          })}
                          open={isAcknowledgePoperOpen}
                          anchorEl={acknowledgePoperOpenAnchorEl}
                          onClose={() => setAcknowledgePoperOpenAnchorEl(null)}
                        />
                      </>
                    )}

                    {/* Tags */}
                    <AuthorizedElement permissions={[AppPermission.FleetAppAccess]}>
                      {message.tags && (
                        <GeneralAttachedTagsDisplay tags={message.tags} defaultIsFolded />
                      )}
                    </AuthorizedElement>
                  </Stack>

                  <Stack direction='column'>
                    {/* ReadBy */}
                    {isShowReadBy && (
                      <Tooltip title='See who read the message'>
                        <AvatarGroup
                          total={message.readByInfo!.length}
                          max={5}
                          sx={{ cursor: "pointer" }}
                          slotProps={{
                            surplus: {
                              sx: { width: 20, height: 20, fontSize: "small" },
                            },
                          }}
                          onClick={() => setIsReadByModalOpen(true)}
                        >
                          {message.readByInfo!.map((readBy, j) => (
                            <ChatParticipantAvatar
                              key={j}
                              chatId={message.chatId}
                              participant={participantsMap[readBy.participantId || ""]}
                              withOnlineStatus={false}
                              withPopover={false}
                              sx={{ width: 20, height: 20 }}
                            />
                          ))}
                        </AvatarGroup>
                      </Tooltip>
                    )}
                  </Stack>
                </Stack>
              </Box>
            )}

            {/* Edit message */}
            {isEditing && (
              <Box>
                <ChatMessageInput
                  chatId={message.chatId!}
                  initialValue={message}
                  placeholder='Enter message...'
                  actionText='Save'
                  autoFocus
                  validation={editValidation}
                  hasUploadedAttachments={
                    (message.attachments && message.attachments.length !== 0) || false
                  }
                  defaultEnableCancel={true}
                  // view-only attachments
                  renderAttachments={() => (
                    <ChatMessageAttachments message={message} />
                    // <FileListView files={FileItem.createManyFrom(message.attachments)} />
                  )}
                  onAction={async (newValue) => await handleEdited(newValue)}
                  onCancel={() => setIsEditing(false)}
                />
              </Box>
            )}
          </Stack>
        </Stack>
      </AppVisibilitySensor>

      {validation.generalError && (
        <FormHelperText error={!!validation.generalError}>{validation.generalError}</FormHelperText>
      )}

      {/* Edit tags */}
      <GeneralAttachedTagsOfEntityEditModal
        open={isEditTagsModalOpen}
        onClose={() => setIsEditTagsModalOpen(false)}
        entityType={TagEntityType.ChatMessage}
        entityId={message.id!}
      />

      {/* ReadBy */}
      <ChatMessageReadByInfoModal
        open={isReadByModalOpen}
        onClose={() => setIsReadByModalOpen(false)}
        message={message}
      />

      {/* View participants */}
      <ChatParticipantsModal
        open={isViewParticipantsModalOpen}
        onClose={() => setIsViewParticipantsModalOpen(false)}
        onUpdated={() => setIsViewParticipantsModalOpen(false)}
        chat={chat}
        allowManage={false}
      />
    </Box>
  );
}
